/* eslint-disable max-lines */
import * as React from "react";
import * as moment from "moment";

import { Authorize } from "reauthorize";
import { Dropdown, Tab } from "semantic-ui-react";

import { toast } from "@teamprevent/common/toasts";
import { AppointmentAndParent, Clinic } from "@teamprevent/common/calendar/models";
import { ClinicLocationTypeName } from "@teamprevent/common/appointment/models";

import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";

import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { useDispatch, useSelector } from "@teamprevent/admin-redux";

import { Calendar } from "../../calendar";
import {
    bookIntoClinic as bookIntoClinicAction,
    showOnlyClinicsWithInsufficientCliniciansChanged,
    clinicTypeChanged,
    clinicLocationTypeChanged,
    focusDateChanged,
    selectedRoomIdsChanged,
    selectedUserIdsChanged,
    chooseAndBook as chooseAndBookAction,
    bookIntoMobileClinic as bookIntoMobileClinicAction,
    loadBookAppointmentTasks,
    clearBookAppointmentTasks
} from "../actions";
import {
    BookAppointmentTaskListModel,
    ApplicationState,
    FaceToFaceTaskTypesBookableIntoMobileClinic,
    BookAppointmentFiltersModel
} from "../models";
import { TriageList } from "../../triage/components/triageList";
import { AppointmentModal } from "../../calendar/components/AppointmentModal";
import { TaskActionSubRoutes } from "../../task/taskActions/TaskActionSubRoutes";
import { ALL_CUSTOMERS } from "../../calendar/clinics/models";
import { CreateTimeNoteModal } from "../../calendar/timeNotes/components/CreateTimeNoteModal";
import { loadCustomers } from "../../crud/customer";
import { getCustomerNamesForBookableCustomerIds } from "../../crud/customer/model";
import { ADMIN_ROLES } from "../../auth";
import { MergedAppointmentModal } from "../appointmentMerging/MergedAppointmentModal";

import { BookAppointmentFilters } from "./BookAppointmentsFilters";
import { BookAdHocAppointment } from "./BookAdHocAppointment";
import { BookClinicAppointment } from "./BookClinicAppointment";
import { BookAppointmentsCards } from "./BookAppointmentsCards";
import { LocationTypeSelectorModal } from "./LocationTypeSelectorModal";
import { BookMultipleAppointments } from "./BookMultipleAppointments";

import { OldBookAppointmentsList } from "./OldBookAppointmentsList";

import { push, Fragment, Location, Link } from "redux-little-router";

interface LocationTypeSelectorModalState {
    open: boolean;
    bookAppointmentTask: BookAppointmentTaskListModel;
    appointment: AppointmentAndParent;
}

const NewBookAppointmentsList: React.FC = () => {
    const dispatch = useDispatch();
    const [locationTypeSelectorModal, setLocationTypeSelectorModal] = React.useState<LocationTypeSelectorModalState | undefined>(undefined);
    const [selectedFilters, setSelectedFilters] = React.useState<BookAppointmentFiltersModel>({
        teams: [],
        products: [],
        customers: [],
        taskTypes: []
    });

    const path = useSelector((state: ApplicationState) => state.router.pathname);
    const customers = useSelector((state: ApplicationState) => state.customers);
    const calendarFilters = useSelector((state: ApplicationState) => state.bookAppointments.calendarFilters);
    const bookTaskIdFromParams = useSelector((state: ApplicationState) => state.router.query?.bookTaskId);
    const bookAppointmentTasks = useSelector((state: ApplicationState) =>
        state.bookAppointments.bookAppointmentTasks.filter(t => !bookTaskIdFromParams || t.id === bookTaskIdFromParams));

    const teams = useSelector((state: ApplicationState) => state.teams);
    const selectedTeams = React.useMemo(() => teams.filter(t => selectedFilters?.teams.indexOf(t.id) !== -1), [selectedFilters?.teams, teams]);
    const filteredBookTasks = React.useMemo(() => bookAppointmentTasks.filter(bookTask =>
        (!selectedTeams.length || selectedTeams.find(team => !!team.customers.find(customer => customer.id === bookTask.customer.id))) &&
        (!selectedFilters?.products.length || selectedFilters?.products.indexOf(bookTask.caseType) !== -1) &&
        (!selectedFilters?.taskTypes.length || selectedFilters?.taskTypes.find(taskType => bookTask.type === taskType)) &&
        (!selectedFilters?.customers.length || selectedFilters?.customers.find(customerId => bookTask.customer?.id === customerId))
    ), [bookAppointmentTasks, selectedFilters?.customers, selectedFilters?.products, selectedFilters?.taskTypes, selectedTeams]);

    React.useEffect(() => {
        loadCustomers();
    }, []);

    const loadTasks = React.useCallback(() => {
        const teamIds = selectedFilters?.teams ?? [];
        const selectedCustomers = selectedFilters?.customers ?? [];
        const taskTypes = selectedFilters?.taskTypes ?? [];
        const products = selectedFilters?.products ?? [];
        const taskId = bookTaskIdFromParams ?? "";

        if (selectedCustomers.length > 0 ||
            teamIds.length > 0 ||
            taskId ||
            taskTypes.length > 0 ||
            products.length > 0)
        {
            dispatch(loadBookAppointmentTasks(selectedCustomers, taskTypes, products, taskId, teamIds));
        }
        else
        {
            dispatch(clearBookAppointmentTasks());
        }
    }, [bookTaskIdFromParams, dispatch, selectedFilters]);

    React.useEffect(() => {
        loadTasks();
    }, [loadTasks]);

    const chooseAndBook = React.useCallback((task) => dispatch(chooseAndBookAction(task)), [dispatch]);

    const onFiltersChanged = React.useCallback((filters: BookAppointmentFiltersModel) => {
        setSelectedFilters(filters);
    }, []);

    const bookAdHocAppointment = React.useCallback((itemId: string, time: moment.Moment, clinicianId?: string) => {
        dispatch(push(
            { pathname: `${path}/${itemId}/create-appointment`, query: { time: time.format(moment.HTML5_FMT.DATETIME_LOCAL), clinicianId: clinicianId ?? "" } },
            { persistQuery: true }
        ));
    }, [dispatch, path]);

    const bookIntoClinic = React.useCallback((bookTaskId: string, appointment: AppointmentAndParent, locationType: ClinicLocationTypeName) => {
        const bookTask = bookAppointmentTasks.find(t => t.id === bookTaskId);
        if (bookTask) {
            dispatch(bookIntoClinicAction(bookTask, appointment, locationType));
        }
    }, [bookAppointmentTasks, dispatch]);

    const bookIntoMobileClinic = React.useCallback((bookTaskId: string, appointment: AppointmentAndParent) => {
        const bookTask = bookAppointmentTasks.find(t => t.id === bookTaskId);
        if (bookTask) {
            dispatch(bookIntoMobileClinicAction(bookTask, appointment));
        }
    }, [bookAppointmentTasks, dispatch]);

    const bookIntoOpenClinic = React.useCallback((bookTaskId: string, appointment: AppointmentAndParent) => {
        const bookTask = bookAppointmentTasks.find(t => t.id === bookTaskId);

        if (bookTask) {
            if (appointment.clinicType === "Health Surveillance") {
                dispatch(push(`${path}/${bookTask.id}/${appointment.id}/book-clinic-appointment`, { persistQuery: true }));
                return;
            }

            dispatch(push(`${path}/${bookTask.id}/${appointment.id}/open-clinic-appointments`, { persistQuery: true }));
        }
    }, [bookAppointmentTasks, dispatch, path]);

    const bookTaskCaseTypeMatchesAppointmentClinicTypeOrIsOpenClinic = (bookTaskCaseType: string, appointment: AppointmentAndParent) => {
        const openOrBookTaskCaseType = ["Open", bookTaskCaseType];
        return openOrBookTaskCaseType.find(type => type === appointment.clinicType || type === appointment.parentAppointment?.clinicType);
    };

    const onSubmitLocationTypeSelectorModal = React.useCallback((location: ClinicLocationTypeName) => {
        if (locationTypeSelectorModal) {
            bookIntoClinic(locationTypeSelectorModal.bookAppointmentTask.id, locationTypeSelectorModal.appointment, location);
            setLocationTypeSelectorModal(undefined);
        }
    }, [bookIntoClinic, locationTypeSelectorModal]);

    const onCancel = React.useCallback(() => setLocationTypeSelectorModal((prevState) => ({ ...prevState, open: false })), []);

    const onHourDrop = React.useCallback((bookTaskId: string, hour: moment.Moment, entityId: string) => {
        bookAdHocAppointment(bookTaskId, hour, entityId);
    }, [bookAdHocAppointment]);

    const isTaskRoute = React.useCallback((l: Location) => !!l.params && !!l.params.taskId, []);

    const isNotTaskRoute = React.useCallback((l: Location) => !!l.params && !l.params.taskId, []);

    const isRestrictedByCustomerId = React.useCallback((parentAppointment: Clinic | undefined, bookTask: BookAppointmentTaskListModel) => {
        const customerIds = parentAppointment?.customerIds ?? [];
        const canBeBooked = customerIds.length === 0 || customerIds.includes(bookTask.customer.id) || customerIds.includes(ALL_CUSTOMERS);
        return !canBeBooked;
    }, []);

    const onHourClick = React.useCallback((time: moment.Moment, clinicianId: string) => {
        dispatch(push({ pathname: `${path}/create-time-note`, query: { time: time.format(moment.HTML5_FMT.DATETIME_LOCAL), clinicianId } }));
    }, [dispatch, path]);

    const onAppointmentClick = React.useCallback((appointment: AppointmentAndParent) => {
        if (!appointment.clinicType) {
            if (appointment?.parentAppointment?.clinicType === "Open" && appointment?.baseAppointment && appointment.customer) {
                dispatch(push(`/book-appointments/mergedappointment/${appointment.id}/${appointment.customer.id}/${appointment.ohCaseId}`));
                return;
            }

            if (appointment && appointment.customer && appointment.customer.id && appointment.ohCaseId)
            {
                dispatch(push(`/book-appointments/appointment/${appointment.id}/${appointment.customer.id}/${appointment.ohCaseId}`));
                return;
            }

            if (appointment.parentAppointment) {
                dispatch(push(`/book-appointments/clinic/${appointment.parentAppointment.id}/appointment/${appointment.id}`));
                return;
            }

            dispatch(push(`/book-appointments/appointment/${appointment.id}`));
        }
    }, [dispatch]);

    const onAppointmentDrop = React.useCallback((bookTaskId: string, appointment: AppointmentAndParent) => {
        const bookTask = bookAppointmentTasks.find(t => t.id === bookTaskId);

        if (!bookTask)
        {
            return;
        }

        if (!bookTaskCaseTypeMatchesAppointmentClinicTypeOrIsOpenClinic(bookTask.caseType, appointment)) {
            toast.error(`This clinic does not accept ${bookTask.caseType} appointments`);
            return;
        }
        if (isRestrictedByCustomerId(appointment.parentAppointment, bookTask)) {
            const customerName = customers.find(c => c.id === bookTask.customer?.id)?.name;
            toast.error(`This clinic does not accept appointments for customer ${customerName}`);

            const customerIds = appointment.parentAppointment?.customerIds ?? [];
            const allowableCustomersNames = getCustomerNamesForBookableCustomerIds(customerIds, customers);
            toast.info(`This clinics accepts appointments for the following customers: ${allowableCustomersNames}`);

            return;
        }
        if (appointment.parentAppointment && !bookTask.locationType && appointment.clinicLocationTypes.length > 1)
        {
            setLocationTypeSelectorModal({ open: true, bookAppointmentTask: bookTask, appointment });
            return;
        }

        const isFaceToFaceTaskTypeBookableIntoMobileClinic =
            FaceToFaceTaskTypesBookableIntoMobileClinic.includes(bookTask.type) &&
            appointment.clinicLocationTypes &&
            appointment.clinicLocationTypes.includes(ClinicLocationTypeName.mobile);

        if (!isFaceToFaceTaskTypeBookableIntoMobileClinic &&
            bookTask.locationType &&
            appointment.clinicLocationTypes &&
            !appointment.clinicLocationTypes.find(l => bookTask.locationType === l)) {
            toast.error(`This clinic does not accept ${bookTask.locationType} appointments`);
            return;
        }
        if (appointment.parentAppointment) {
            bookIntoClinic(bookTaskId, appointment, bookTask.locationType ?? appointment.clinicLocationTypes[0]);
            return;
        }
        if (appointment.clinicLocationTypes && appointment.clinicLocationTypes.includes(ClinicLocationTypeName.mobile)) {
            bookIntoMobileClinic(bookTaskId, appointment);
            return;
        }

        bookIntoOpenClinic(bookTaskId, appointment);
    },[bookAppointmentTasks, bookIntoClinic, bookIntoMobileClinic, bookIntoOpenClinic, customers, isRestrictedByCustomerId]);

    const cardsComponent = (<BookAppointmentsCards bookAppointmentTasks={filteredBookTasks} onChooseAndBook={chooseAndBook} />);
    const filterComponent = (<BookAppointmentFilters onFiltersChanged={onFiltersChanged} />);

    const onAppointmentModalClose = React.useCallback(() => dispatch(push("/book-appointments")), [dispatch]);
    const onDateChanged = React.useCallback((date: moment.Moment) => dispatch(focusDateChanged(date)), [dispatch]);
    const onSelectedRoomIdsChanged = React.useCallback((roomIds: string[]) => dispatch(selectedRoomIdsChanged(roomIds)), [dispatch]);
    const onSelectedUserIdsChanged = React.useCallback((userIds: string[]) => dispatch(selectedUserIdsChanged(userIds)), [dispatch]);
    const onClinicTypeChanged = React.useCallback((clinicType: string) => dispatch(clinicTypeChanged(clinicType)), [dispatch]);
    const onClinicLocationTypeChanged = React.useCallback((payload: ClinicLocationTypeName) => dispatch(clinicLocationTypeChanged(payload)), [dispatch]);
    const onShowOnlyClinicsWithInsufficientCliniciansChanged =
        React.useCallback((payload: boolean) => dispatch(showOnlyClinicsWithInsufficientCliniciansChanged(payload)), [dispatch]);

    const calendarComponent = (showClinics: boolean, showAppointmentCount: boolean) => {
        return (<Calendar
            showClinics={showClinics}
            onAppointmentDrop={onAppointmentDrop}
            onHourDrop={onHourDrop}
            onHourClick={onHourClick}
            onAppointmentClick={onAppointmentClick}
            showOnlyClinicsWithInsufficientClinicians={calendarFilters.showOnlyClinicsWithInsufficientClinicians}
            onShowOnlyClinicsWithInsufficientCliniciansChanged={onShowOnlyClinicsWithInsufficientCliniciansChanged}
            clinicLocationType={calendarFilters.clinicLocationType}
            onClinicLocationTypeChanged={onClinicLocationTypeChanged}
            clinicType={calendarFilters.clinicType}
            onClinicTypeChanged={onClinicTypeChanged}
            focusDate={calendarFilters.focusDate}
            onDateChange={onDateChanged}
            selectedUserIds={calendarFilters.selectedUserIds}
            onSelectedUserIdsChanged={onSelectedUserIdsChanged}
            selectedRoomIds={calendarFilters.selectedRoomIds}
            onSelectedRoomIdsChanged={onSelectedRoomIdsChanged}
            showAppointmentCount={showAppointmentCount}
            showAllUsersOption={true}
            showGapsAndClinicsFilters={true}
        />);
    };

    const panes = [
        { menuItem: "Clinics", render: () => calendarComponent(true, true) },
        { menuItem: "Rooms and Clinicians", render: () => calendarComponent(false, false) }
    ];

    return (
        <TriageList cards={cardsComponent} filter={filterComponent} className="book-appointments-list">
            <Fragment forRoute="/appointment/:appointmentId/:customerId/:ohCaseId" withConditions={isTaskRoute}>
                <TaskActionSubRoutes />
            </Fragment>
            <Fragment forRoute="/mergedappointment/:appointmentId/:customerId/:ohCaseId">
                <MergedAppointmentModal onModalClose={onAppointmentModalClose} />
            </Fragment>
            <Fragment forRoute="/:bookTaskId/create-appointment">
                <BookAdHocAppointment />
            </Fragment>
            <Fragment forRoute="/:bookTaskId/:clinicId/book-clinic-appointment">
                <BookClinicAppointment />
            </Fragment>
            <Fragment forRoute="/:bookTaskId/:clinicId/open-clinic-appointments">
                <BookMultipleAppointments onModalClose={onAppointmentModalClose} loadTasks={loadTasks} />
            </Fragment>
            <Fragment forRoute="/create-time-note">
                <CreateTimeNoteModal />
            </Fragment>
            <Fragment forRoute="/" withConditions={isNotTaskRoute}>
                <>
                    <Authorize authorize={[ADMIN_ROLES.ADMINISTRATOR.name, ADMIN_ROLES.SENIOR_ADMINISTRATOR.name, ADMIN_ROLES.SYSTEM_ADMIN.name]}>
                        <Dropdown className="icon create-button" labeled button text="Create" icon="add" direction="left">
                            <Dropdown.Menu>
                                <Dropdown.Item as={Link} text="Create Clinic" href={`${path}/create`} />
                                <Dropdown.Item as={Link} text="Create Mobile Clinic" href={`${path}/create-mobile-clinic`} />
                            </Dropdown.Menu>
                        </Dropdown>
                    </Authorize>
                    <Tab panes={panes} defaultActiveIndex={1} />
                    <AppointmentModal onModalClose={onAppointmentModalClose} />
                    <LocationTypeSelectorModal
                        {...locationTypeSelectorModal}
                        onCancel={onCancel}
                        onSubmit={onSubmitLocationTypeSelectorModal}
                    />
                </>
            </Fragment>
        </TriageList>
    );
};

export const BookAppointmentsList = () =>
    getFeatureToggles()[FeatureToggleNames.EnableBookAppointmentsPageImprovements]
        ? <NewBookAppointmentsList />
        // eslint-disable-next-line deprecation/deprecation
        : <OldBookAppointmentsList />;
